export const FormMode = {
  CREATE: "create",
  UPDATE: "update",
  DUPLICATE: "duplicate",
};

export enum OFFER_TYPES {
  SPECIAL = "SPECIAL_OFFER",
  TOUR = "TOUR_OFFER",
  EVENT = "EVENT_OFFER",
}

export const enum BookingStatus {
  ALL = "",
  WAITING = "waiting",
  CONFIRMED = "confirmed",
  CANCELED = "canceled",
  REFUSED = "refused",
  PASSED = "passed",
  CASH_PRE_PAYMENT = "cash_pre_payment",
  PRE_PAYMENT = "pre_payment",
}

export enum BookingStatusLs {
  PRE_PAYMENT = "pre_payment",
  CASH_PRE_PAYMENT = "cash_pre_payment",
  WAITING = "waiting",
  CONFIRMED = "confirmed",
  CANCELED = "canceled",
  REFUSED = "refused",
  REQ_SCHEDULE = "req_schedule",
}

export const BookingStatusLabels = {
  [BookingStatusLs.PRE_PAYMENT]: "En attente de paiement",
  [BookingStatusLs.CASH_PRE_PAYMENT]: "En attente de paiement",
  [BookingStatusLs.WAITING]: "En attente",
  [BookingStatusLs.CONFIRMED]: "Confirmée",
  [BookingStatusLs.CANCELED]: "Annulée",
  [BookingStatusLs.REFUSED]: "Refusée",
  [BookingStatusLs.REQ_SCHEDULE]: "En attente de réservation",
};

// helpers/enums.ts or similar
export const BookingStatusDisplayValues = {
  confirmed: "Confirmé",
  waiting: "En attente",
  canceled: "Annulé",
  refused: "Refusé",
  pre_payment: "Prépaiement",
  cash_pre_payment: "Paiement sur place",
  req_schedule: "Demande de programmation",
  passed: "Passé",
};

export enum BookingStatusDisplayValue {
  ALL = "Toutes",
  WAITING = "En attente",
  CONFIRMED = "Confirmée",
  CANCELED = "Annulée",
  REFUSED = "Refusée",
  PASSED = "Passée",
  CASH_PRE_PAYMENT = "En attente de paiement",
}

export enum PaymentOptions {
  IN_PLACE = "in_place",
  CREDIT_CARD = "credit_card",
  WAFACASH = "wafacash",
  STRIPE = "stripe",
  USER_BALANCE = "user_balance",
}

export enum BookingTypes {
  PLACE_BOOKING = "PLACE_BOOKING",
  EVENT_BOOKING = "EVENT_BOOKING",
  TOUR_BOOKING = "TOUR_BOOKING",
  SPECIAL_OFFER_BOOKING = "SPECIAL_OFFER_BOOKING",
  PACK_BOOKING = "PACK_BOOKING",
  ACTIVITY_BOOKING = "ACTIVITY_BOOKING",
}

export enum DriverProfileStatus {
  IN_REVIEW = "in_review",
  APPROVED = "approved",
  DECLINED = "declined",
  BLOCKED = "blocked",
}

export enum DriverProfileStatusDisplayValue {
  IN_REVIEW = "En attente",
  APPROVED = "Acceptée",
  DECLINED = "Refusée",
  BLOCKED = "Bloquee",
}

export const DriverStatusLabels = {
  [DriverProfileStatus.IN_REVIEW]: "En attente",
  [DriverProfileStatus.APPROVED]: "Acceptée",
  [DriverProfileStatus.DECLINED]: "Refusée",
  [DriverProfileStatus.BLOCKED]: "Bloquée",
};

export enum RideStatus {
  SEARCHING_FOR_DRIVERS = "searching_for_drivers",
  NO_DRIVER_FOUND = "no_driver_found",
  WAITING_PICKUP = "waiting_pickup",
  ON_GOING = "on_going",
  DRIVER_CANCELLED = "driver_cancelled",
  USER_CANCELLED = "user_cancelled",
  ADMIN_CANCELLED = "admin_cancelled",
  COMPLETED = "completed",
}

export enum RideHistoryType {
  ACTION = "action",
  DRIVER_LOG = "driver_log",
  USER_LOG = "user_log",
  CHAT = "chat",
}

interface BaseRideHistoryData {
  type: RideHistoryType;
}

// --- Action-specific data ---
export enum RideActionType {
  OPEN_NAVIGATION_APP = "open_navigation_app",
  INITIATE_CALL = "initiate_call",
  APP_STATUS_CHANGED = "app_status_changed",
  ARRIVED_AT_PICKUP = "arrived_at_pickup",
  USER_IS_COMING = "user_is_coming",
  START_RIDE = "start_ride",
  COMPLETE_RIDE = "complete_ride",
  USER_CANCEL_RIDE = "user_cancel_ride",
  DRIVER_CANCEL_RIDE = "driver_cancel_ride",
  ADMIN_CANCEL_RIDE = "admin_cancel_ride",
  FOUND_DRIVER = "found_driver",
  RETRY_RIDE_REQ = "retry_ride_req",
  SEARCH_NEW_DRIVER = "search_new_driver",
  NO_DRIVER_FOUND = "no_driver_found",
}

interface ActionOpenNavigationApp extends BaseRideHistoryData {
  action: RideActionType.OPEN_NAVIGATION_APP;
  by: "driver" | "user";
  navigationApp: "waze" | "google_maps" | "apple_maps";
}

interface ActionInitiateCall extends BaseRideHistoryData {
  action: RideActionType.INITIATE_CALL;
  by: "driver" | "user";
  callType: "whatsapp_call" | "phone_call";
}

interface ActionAppStatusChanged extends BaseRideHistoryData {
  action: RideActionType.APP_STATUS_CHANGED;
  by: "driver" | "user";
  appStatus: "paused" | "resumed";
}

interface ActionArrivedAtPickup extends BaseRideHistoryData {
  action:
    | RideActionType.ARRIVED_AT_PICKUP
    | RideActionType.USER_IS_COMING
    | RideActionType.START_RIDE
    | RideActionType.COMPLETE_RIDE;
  lat?: number;
  lng?: number;
  isSystem?: boolean;
  reason?: string;
  admin?: {
    id: string;
    email: string;
  };
}

interface ActionCancelRide extends BaseRideHistoryData {
  action:
    | RideActionType.USER_CANCEL_RIDE
    | RideActionType.DRIVER_CANCEL_RIDE
    | RideActionType.ADMIN_CANCEL_RIDE;
  lat?: number;
  lng?: number;
  isSystem?: boolean;
  reason?: string;
  admin?: {
    id: string;
    email: string;
  };
}

interface ActionFoundDriver extends BaseRideHistoryData {
  action:
    | RideActionType.FOUND_DRIVER
    | RideActionType.RETRY_RIDE_REQ
    | RideActionType.SEARCH_NEW_DRIVER
    | RideActionType.NO_DRIVER_FOUND;
  lat?: number;
  lng?: number;
  isSystem?: boolean;
  reason?: string;
}

interface ChatMessage extends BaseRideHistoryData {
  msgType: "text";
  sender: "driver" | "user";
  data: string;
  seen: boolean;
  dateTime: string;
  isSystem?: boolean;
}

export type RideHistoryData =
  | ActionOpenNavigationApp
  | ActionInitiateCall
  | ActionAppStatusChanged
  | ActionArrivedAtPickup
  | ActionCancelRide
  | ActionFoundDriver
  | ChatMessage;

// --- RideHistory Entity ---

export interface RideHistory {
  id: number;
  rideId: number; // optional if available
  type: RideHistoryType;
  rideStatus: RideStatus;
  data: RideHistoryData;
  createdAt: string; // ISO date string like "2025-06-30T07:32:24.876Z"
}

export enum RidecancelReason {
  CANCEL_REASON_1 = "cancel_reason_1",
  CANCEL_REASON_2 = "cancel_reason_2",
  CANCEL_REASON_3 = "cancel_reason_3",
  CANCEL_REASON_4 = "cancel_reason_4",
}

export const RidecancelReasonLabels = {
  [RidecancelReason.CANCEL_REASON_1]:
    "Le chauffeur a mis trop de temps à être désigné",
  [RidecancelReason.CANCEL_REASON_2]: "Je n'ai plus besoin de ce déplacement",
  [RidecancelReason.CANCEL_REASON_3]:
    "Le chauffeur a refusé de se rendre sur place.",
  [RidecancelReason.CANCEL_REASON_4]: "Je veux changer de localisation.",
};

export const RideFoundDriverSourceLabels = {
  "in-app-card": "Acceptée dans l’app",
  overlay: "Acceptée via la pop-up",
};

export const RideStatusLabels = {
  [RideStatus.SEARCHING_FOR_DRIVERS]: "Recherche de conducteurs",
  [RideStatus.NO_DRIVER_FOUND]: "Aucun conducteur trouvé",
  [RideStatus.WAITING_PICKUP]: "En attente de prise en charge",
  [RideStatus.ON_GOING]: "En cours",
  [RideStatus.ADMIN_CANCELLED]: "Admin annulé",
  [RideStatus.DRIVER_CANCELLED]: "Conducteur annulé",
  [RideStatus.USER_CANCELLED]: "Client annulé",
  [RideStatus.COMPLETED]: "Terminé",
};

export enum TransactionType {
  TOP_UP = "top_up",
  RIDE_PRECETANGE = "ride_precetange",
  VAT = "vat",
  REFUND = "refund",
  RIDE_BONUS = "ride_bonus",
  VENEZ_TOP_UP = "venez_top_up",
  RIDE_DISCOUNT = "ride_discount",
  USER_BALANCE_PAYMENT = "user_balance_payment",
  USER_TOP_UP = "user_top_up",
}

export const TransactionTypeLabel = {
  [TransactionType.TOP_UP]: "Recharge",
  [TransactionType.RIDE_PRECETANGE]: "Pourcentage de course",
  [TransactionType.VAT]: "TVA",
  [TransactionType.REFUND]: "Remboursement",
  [TransactionType.RIDE_BONUS]: "Bonus de trajet",
  [TransactionType.VENEZ_TOP_UP]: "Venez recharge",
  [TransactionType.RIDE_DISCOUNT]: "Paiement de la réduction",
  [TransactionType.USER_BALANCE_PAYMENT]: "Paiement avec solde",
  [TransactionType.USER_TOP_UP]: "Recharge Utilisateur",
};

export enum TransactionStatus {
  COMPLETED = "completed",
  WAITING_FOR_PAYMENT = "waiting_for_payment",
  CANCELLED = "cancelled",
  PENDING = "pending",
}

export const TransactionStatusLabel = {
  [TransactionStatus.COMPLETED]: "Terminé",
  [TransactionStatus.WAITING_FOR_PAYMENT]: "En attente de paiement",
  [TransactionStatus.CANCELLED]: "Annulée",
  [TransactionStatus.PENDING]: "Pending",
};

export enum TranscationPaymentMethods {
  LOCAL = "local",
  WAFACASH = "wafacash",
  CREDIT_CARD = "credit_card",
}

export enum Periods {
  currentMonth = "currentMonth",
  last3Months = "last3Months",
  last6Months = "last6Months",
  currentYear = "currentYear",
  lastYear = "lastYear",
  twoYearsAgo = "2YearsAgo",
  last7Days = "last7Days",
  yesterday = "yesterday",
  today = "today",
}

export enum RangeEnum {
  TODAY = "today",
  THIS_WEEK = "this_week",
  THIS_MONTH = "this_month",
  LAST_3_MONTHS = "last_3_months",
  all = "",
}

export type LatLng = { lat: number; lng: number };
export type GeoMode = "points" | "zones";
export interface PlacePoint extends LatLng {
  id: string;
  name?: string;
}

export enum ActivityStatus {
  DRAFT = "draft",
  PENDING_REVIEW = "pending_review",
  APPROVED = "approved",
  REJECTED = "rejected",
}
